/**
 * The notification bell, computed — never stored: unread = messages by others after my read marker
 * (comment_reads for event boards, chat_reads for chats), at most NOTIFY_WINDOW_MS back. The server
 * aggregates the unread messages per (thread, author) in SQL and groups those rows here into ONE item per
 * thread ("Bjarki og Ingólfur skrifuðu á Æfing"), newest first. Pure, so it is unit-tested.
 */
import type { Section } from './audience';
import type { NotificationItemDTO } from './dto/chat';
import type { EventKind, EventStatus } from './dto/events';

/** How far back unread messages count (older ones are left alone: the bell is about what is new). */
export const NOTIFY_WINDOW_MS = 14 * 86_400_000;
/** Authors listed per item (the rest are counted). */
export const NOTIFY_MAX_AUTHORS = 5;
/** Items in the bell. */
export const NOTIFY_MAX_ITEMS = 50;
/** Preview length (characters). */
export const PREVIEW_MAX = 140;

/** Unread messages of one author in one thread (one row of the server's GROUP BY). */
export interface UnreadAuthorRow {
  kind: 'event' | 'chat';
  threadId: string;
  authorId: string;
  authorName: string;
  authorSection: Section | null;
  authorAvatarRev: number;
  /** Unread messages by this author in the thread. */
  count: number;
  /** Their newest unread message. */
  lastAt: number;
  lastBody: string;
  /** kind 'event': the event (same on every row of the thread). */
  event?: { title: string; kind: EventKind; startsAt: number; status: EventStatus };
}

/** One line: whitespace collapsed, cut at `max` characters with an ellipsis. */
export function excerpt(s: string, max = PREVIEW_MAX): string {
  const one = s.replace(/\s+/g, ' ').trim();
  return one.length > max ? `${one.slice(0, max - 1).trimEnd()}…` : one;
}

/** Groups unread (thread, author) rows into one item per thread, newest thread first. */
export function groupNotifications(rows: readonly UnreadAuthorRow[], maxItems = NOTIFY_MAX_ITEMS): NotificationItemDTO[] {
  const threads = new Map<string, UnreadAuthorRow[]>();
  for (const r of rows) {
    if (r.count <= 0) continue;
    const key = `${r.kind}:${r.threadId}`;
    const list = threads.get(key);
    if (list) list.push(r);
    else threads.set(key, [r]);
  }
  const items: NotificationItemDTO[] = [];
  for (const list of threads.values()) {
    // Newest author first; ties by name so the order never flickers between polls.
    const byAuthor = new Map<string, UnreadAuthorRow>();
    for (const r of list) {
      const cur = byAuthor.get(r.authorId);
      byAuthor.set(r.authorId, cur ? { ...cur, count: cur.count + r.count, ...(r.lastAt > cur.lastAt ? { lastAt: r.lastAt, lastBody: r.lastBody } : {}) } : r);
    }
    const authors = [...byAuthor.values()].sort((a, b) => b.lastAt - a.lastAt || a.authorName.localeCompare(b.authorName, 'is'));
    const newest = authors[0]!;
    const first = list[0]!;
    const item: NotificationItemDTO = {
      kind: first.kind,
      id: first.threadId,
      unread: authors.reduce((n, a) => n + a.count, 0),
      latestAt: newest.lastAt,
      authors: authors.slice(0, NOTIFY_MAX_AUTHORS).map((a) => ({
        id: a.authorId,
        fullName: a.authorName,
        section: a.authorSection,
        avatarRev: a.authorAvatarRev,
      })),
      authorCount: authors.length,
      preview: { authorName: newest.authorName, body: excerpt(newest.lastBody) },
    };
    const ev = list.find((r) => r.event)?.event;
    if (first.kind === 'event' && ev) item.event = ev;
    items.push(item);
  }
  items.sort((a, b) => b.latestAt - a.latestAt || a.id.localeCompare(b.id));
  return items.slice(0, maxItems);
}

/** Unread messages in all items (app badge). */
export function notificationTotal(items: readonly Pick<NotificationItemDTO, 'unread'>[]): number {
  return items.reduce((n, it) => n + it.unread, 0);
}

/** A full recompute at least this often, whatever the change check says (event visibility, edits, …). */
export const NOTIFY_RECHECK_MS = 15 * 60_000;
/**
 * Writes carry the time their request STARTED: a write still running while the bell was computed lands
 * with a time before that computation. Changes this close to the stamp always count as "changed".
 */
export const NOTIFY_SKEW_MS = 30_000;

/**
 * May the bell answer `unchanged` to a poll? `since` = the stamp (server time) of the last full answer,
 * `latest` = the newest change that could affect this member's bell (see latestActivity on the server).
 */
export function shouldSkipRecompute(since: number, latest: number, now: number): boolean {
  if (since > now || now - since > NOTIFY_RECHECK_MS) return false;
  return latest <= since - NOTIFY_SKEW_MS;
}
